import { ColumnProps } from "antd/lib/table";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Tag } from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import BinContext from "../../../../context/wms/bin/context";
import { IBin } from "../../../../models/wms/bin/response";
import { BinPosition } from "../../../../models/wms/enums";
import Space from "../../../general/antd/space";
import ViewBtn from "../../../general/table-components/actions/view-btn";
import EditBtn from "../../../general/table-components/actions/edit-btn";
import DeleteBtn from "../../../general/table-components/actions/delete-btn";
import { ISort, IStaticFilter } from "../../../../models/base/base-query";
import { tableOnChange } from "../../../../utils/helpers/table-sorts-filters";
import SortableTable from "../../../general/table-components/sortable-table";
import EditablePickSequence from "../../../general/table-components/editable-pick-sequence";
import styles from "./styles.module.scss";

interface IProps {
  sortEnabled?: boolean;
}

const BinTable: React.FC<IProps> = ({ sortEnabled = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list, actions, loading, query } = useContext(BinContext);
  const [dataSource, setDataSource] = useState(list?.data ?? []);

  useEffect(() => {
    if (list?.data) {
      setDataSource(list.data);
    }
  }, [list?.data]);

  const handleDragEnd = async (activeId: number | string, overId: number | string, oldIndex: number, newIndex: number) => {
    const overItem = dataSource.find((item) => item.id === overId);
    if (overItem) {
      await actions.reorderBin(Number(activeId), overItem.pickSequence);
    }
  };

  const columns: ColumnProps<IBin>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      sorter: {
        multiple: 1,
      },
      width: 80,
      align: "center",
    },
    {
      title: t("name"),
      dataIndex: "name",
      align: "center",
      key: "name",
      width: 150,
      sorter: {
        multiple: 2,
      },
      render: (value: string) => value || "-",
    },
    {
      title: t("shelf"),
      dataIndex: "shelf",
      align: "center",
      key: "shelfId",
      width: 180,
      sorter: {
        multiple: 2,
      },
      render: (_: any, record: IBin) =>
        record.shelf?.name || record.shelfId || "-",
    },
    {
      title: t("bin_position"),
      dataIndex: "binPosition",
      align: "center",
      key: "binPosition",
      sorter: {
        multiple: 3,
      },
      width: 120,
      render: (value: BinPosition) => {
        if (value === BinPosition.LEFT) return t("position_left");
        if (value === BinPosition.CENTER) return t("position_center");
        if (value === BinPosition.RIGHT) return t("position_right");
        return "-";
      },
    },
    {
      title: t("scanable_code"),
      dataIndex: "scanableCode",
      align: "center",
      key: "scanableCode",
      width: 200,
      sorter: {
        multiple: 4,
      },
      render: (value: string) => value || "-",
    },
    {
      title: t("pick_sequence"),
      dataIndex: "pickSequence",
      align: "center",
      key: "pickSequence",
      sorter: {
        multiple: 5,
      },
      width: 120,
      render: (value: number, record: IBin) =>
        sortEnabled ? (
          <EditablePickSequence
            value={value}
            onSave={async (newValue) => {
              await actions.reorderBin(record.id, newValue);
            }}
          />
        ) : (
          value
        ),
    },
    {
      title: t("is_active"),
      dataIndex: "isActive",
      align: "center",
      key: "isActive",
      sorter: {
        multiple: 6,
      },
      width: 120,
      render: (value: boolean) =>
        value ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            {t("active")}
          </Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">
            {t("inactive")}
          </Tag>
        ),
    },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      align: "center",
      key: "createdAt",
      sorter: {
        multiple: 7,
      },
      width: 150,
      render: (value: Date) => value ? new Date(value).toLocaleDateString() : "-",
    },
    {
      title: t("actions"),
      width: 200,
      dataIndex: "",
      align: "center",
      key: "actions",
      render: (_: any, record) => (
        <Space>
          <ViewBtn
            onClick={(e) => {
              const url = `/wms/bins/details/${record?.id}`;
              if (e.ctrlKey || e.metaKey) {
                window.open(url, "_blank");
              } else {
                navigate(url);
              }
            }}
          />
          <EditBtn
            onClick={(e) => {
              const url = `/wms/bins/update/${record?.id}`;
              if (e.ctrlKey || e.metaKey) {
                window.open(url, "_blank");
              } else {
                navigate(url);
              }
            }}
          />
          <DeleteBtn
            onConfirm={async () => {
              await actions.deleteBin(record?.id);
            }}
            loading={loading.includes("delete")}
          />
        </Space>
      ),
    },
  ];

  // Filters && Sorts
  let tableFiltersProps = {};
  const [sorts, setSorts] = useState<ISort[]>([]);
  const [, setStaticFilters] = useState<IStaticFilter[]>(
    query?.filters ?? []
  );

  useEffect(() => {
    sorts.length > 0 &&
      actions.setQuery({
        ...query,
        orders: sorts,
      });
  }, [sorts]);

  return (
    <SortableTable<IBin>
      dataSource={dataSource}
      columns={columns}
      loading={loading.includes("list") || list == undefined}
      showPagination={true}
      total={list?.total}
      pageSize={query.perPage}
      page={query.page}
      onPaginationChange={(page, pageSize) => {
        actions.setQuery({
          ...query,
          page,
          perPage: pageSize,
        });
      }}
      onDragEnd={handleDragEnd}
      showActiveToggle={false}
      rowKey="id"
      className={styles.table}
      sortEnabled={sortEnabled}
      onChange={(_, tableFilters, tabelSorters) => {
        tableOnChange(
          tableFilters,
          tabelSorters,
          tableFiltersProps,
          setSorts,
          setStaticFilters
        );
      }}
    />
  );
};

export default BinTable;
